// Samsung-Zugriff auf die geraeteinternen Einstellungen.
//
// Drei Wege, in dieser Reihenfolge:
//
//  1. `tizen.tvaudiocontrol` - dokumentierte Tizen-TV-API fuer Lautstaerke und
//     Stumm. Der einzige Teil, der auf jedem Geraet traegt (auf einem BU800
//     gemessen: nur diese beiden Werte kamen zurueck).
//  2. `b2bapis.b2bcontrol` bzw. `webapis.systemcontrol` - die B2B-API der
//     Hospitality-Firmware. Aufgerufen wird ausschliesslich, was das Objekt
//     WIRKLICH hat: die Methodennamen werden vorher geprueft (siehe
//     methodNames), nie blind versucht.
//  3. `webapis.settings.getItemValue/setItemValue` - der Weg, ueber den diese
//     App schon die OSD-Sprache setzt. Belegt ist die Menue-Id 'osd-language',
//     die uebrigen sind Kandidaten.
//
// Aufrufkonvention der B2B-Getter: Samsung ist da nicht einheitlich - manche
// liefern direkt, manche erwarten Callbacks (vgl. deleteAppPrivateInfoNow in
// helpers/Cast/Tizen.js). Beides wird versucht, in dieser Reihenfolge, mit
// kurzem Zeitbudget fuer die Callback-Variante.

import { OSD_LOCALE, systemLocaleValue, normalizeLocale } from '@/helpers/OsdLanguage';
import { SETTING_TYPE } from './catalog';

// Zeitbudget fuer einen Callback-Getter. Kurz: bleibt der Callback aus, ist der
// Weg fuer dieses Modell ohnehin nicht brauchbar.
const CALLBACK_TIMEOUT_MS = 1500;

function settingsApi() {
  if (typeof window === 'undefined') return undefined;
  const w = window.webapis;
  return w && w.settings ? w.settings : undefined;
}

function audioApi() {
  if (typeof window === 'undefined') return undefined;
  const t = window.tizen;
  return t && t.tvaudiocontrol ? t.tvaudiocontrol : undefined;
}

// b2bapis.b2bcontrol ist die aeltere Variante, systemcontrol die aktuelle -
// dieselbe Reihenfolge wie in helpers/Cast/Tizen.js.
function b2bApis() {
  if (typeof window === 'undefined') return [];
  const out = [];
  const w = window.webapis;
  if (w && w.systemcontrol) out.push({ name: 'webapis.systemcontrol', api: w.systemcontrol });
  if (window.b2bapis && window.b2bapis.b2bcontrol)
    out.push({ name: 'b2bapis.b2bcontrol', api: window.b2bapis.b2bcontrol });
  return out;
}

// Die Funktionsnamen eines nativen Objekts. Wie in Cast/Tizen.js: manche
// WebIDL-Objekte legen ihre Methoden nur auf dem Prototyp ab, deshalb der
// for..in - und Zugriffe, die werfen, werden uebergangen.
function methodNames(api) {
  const out = [];
  if (!api) return out;
  for (const k in api) {
    try {
      if (typeof api[k] === 'function') out.push(k);
    } catch (e) {
      /* manche Properties werfen beim Zugriff - dann eben ohne */
    }
  }
  return out.sort();
}

function hasMethod(api, name) {
  try {
    return !!api && typeof api[name] === 'function';
  } catch (e) {
    return false;
  }
}

// Ein Rueckgabewert, mit dem sich arbeiten laesst. Objekte kommen als {value}
// vor (so liefert auch getItemValue auf manchen Firmwares).
function usable(raw) {
  if (raw === null || raw === undefined || raw === '') return undefined;
  if (typeof raw === 'function') return undefined;
  if (typeof raw === 'object') return raw.value === undefined ? undefined : raw.value;
  return raw;
}

/**
 * Einen nativen Getter aufrufen - erst direkt, dann mit Callbacks.
 *
 * Der direkte Versuch zuerst, weil er nichts kostet: liefert die Methode
 * sofort einen Wert, ist die Sache erledigt. Wirft sie (zu wenige Argumente)
 * oder liefert nichts, folgt die Callback-Variante mit Zeitbudget - ohne das
 * haengt eine Firmware, die den Callback nie ruft, die ganze Abfrage auf.
 */
function errText(e) {
  if (!e) return 'ohne Angabe';
  if (e.code !== undefined && e.code !== null) return 'Fehler ' + e.code;
  return String(e.name || e.message || e);
}

function callGetter(api, name) {
  let direct;
  let firstError;
  try {
    direct = usable(api[name]());
  } catch (e) {
    firstError = e;
    direct = undefined;
  }
  if (direct !== undefined) return Promise.resolve({ value: direct });

  return new Promise((resolve) => {
    let settled = false;
    const finish = (r) => {
      if (settled) return;
      settled = true;
      resolve(r);
    };
    const timer = setTimeout(() => finish({ error: 'keine Antwort' }), CALLBACK_TIMEOUT_MS);
    try {
      api[name](
        function(res) {
          clearTimeout(timer);
          const v = usable(res);
          finish(v === undefined ? { error: 'Antwort ohne Wert' } : { value: v });
        },
        function(err) {
          clearTimeout(timer);
          finish({ error: errText(err || firstError) });
        }
      );
    } catch (e) {
      clearTimeout(timer);
      finish({ error: errText(e || firstError) });
    }
  });
}

/**
 * Einen nativen Setter aufrufen - erst direkt, dann mit Callbacks.
 *
 * Dieselbe Uneinheitlichkeit wie bei den Gettern: `tizen.tvaudiocontrol`
 * nimmt den Wert direkt, die systemcontrol-Setter erwarten je nach Firmware
 * noch Erfolgs- und Fehler-Callback. Ein Aufruf mit zu wenigen Argumenten
 * wirft, also ist der Wurf hier das Signal, es anders zu versuchen.
 */
function callSetter(api, name, value) {
  try {
    api[name](value);
    return Promise.resolve({ ok: true });
  } catch (e) {
    const direct = String((e && (e.message || e.name)) || e);
    return new Promise((resolve) => {
      let settled = false;
      const finish = (r) => {
        if (settled) return;
        settled = true;
        resolve(r);
      };
      const timer = setTimeout(() => finish({ ok: false, detail: direct + ' (auch ohne Antwort mit Callback)' }), CALLBACK_TIMEOUT_MS);
      try {
        api[name](
          value,
          function() {
            clearTimeout(timer);
            finish({ ok: true });
          },
          function(err) {
            clearTimeout(timer);
            finish({ ok: false, detail: String((err && (err.message || err.name)) || err) });
          }
        );
      } catch (e2) {
        clearTimeout(timer);
        finish({ ok: false, detail: direct });
      }
    });
  }
}

// Sprachkuerzel <-> Tizen-Locale. Der Draht spricht Kuerzel ('de'), das Geraet
// Locale ('de_DE.utf8') - siehe helpers/OsdLanguage.
//
// Angenommen wird beides: das Kuerzel ('de') UND die Locale selbst
// ('de_DE.utf8'). Letztere schickt der SocketAdmin, sobald das Geraet die
// Auswahlliste des Menuepunkts meldet - deren Werte SIND Locales. Vorher
// lehnte das Setzen genau diese Werte mit "keine Locale fuer de_DE.utf8
// hinterlegt" ab. Eine Locale geht unveraendert (nur vereinheitlicht) an die
// Firmware; ob sie sie kennt, zeigt das Zuruecklesen.
function localeFor(code) {
  const raw = String(code || '');
  return OSD_LOCALE[raw.toLowerCase()] || normalizeLocale(raw) || '';
}

function codeForLocale(locale) {
  const keys = Object.keys(OSD_LOCALE);
  for (let i = 0; i < keys.length; i += 1) {
    // ch_de teilt sich die Locale mit de - das erste Kuerzel gewinnt, und das
    // ist die Hauptsprache. Genau die will der Admin sehen.
    if (OSD_LOCALE[keys[i]] === locale) return keys[i];
  }
  return locale;
}

// Das B2B-Methodenpaar zu einem Katalog-Eintrag, oder null. Geprueft wird die
// Existenz - deshalb sind die Namensstaemme im Katalog gefahrlos geraten.
function b2bPair(entry) {
  const stems = (entry.tizen && entry.tizen.b2b) || [];
  const apis = b2bApis();
  for (let a = 0; a < apis.length; a += 1) {
    for (let i = 0; i < stems.length; i += 1) {
      const getter = 'get' + stems[i];
      if (!hasMethod(apis[a].api, getter)) continue;
      const setter = 'set' + stems[i];
      return {
        api: apis[a].api,
        getter: getter,
        setter: hasMethod(apis[a].api, setter) ? setter : null,
        via: apis[a].name + '.' + getter,
      };
    }
  }
  return null;
}


// --- Sonde in das Einstellungs-Menue ---------------------------------------
//
// webapis.settings adressiert die Menuepunkte des TV ueber Ids wie
// 'auto-power-off'. Eine Liste ALLER Ids gibt die API nicht heraus - es gibt
// nur getFirstVisibleChildItemByParentId, aber keinen Weg zum naechsten
// Geschwister, ein Baumlauf ist damit nicht moeglich.
//
// Also andersherum: eine Liste plausibler Ids durchprobieren und melden, welche
// antworten. Auf Samsung kostet das fast nichts, weil getItemValue SYNCHRON
// ist - anders als auf LG, wo jeder unbekannte Aufruf in einen Timeout lief.
// Gemessen auf einem U800F: 'auto-power-off' und 'screen-saver' antworten,
// beide mit {value, osdName}.
//
// Was antwortet, steht als `menuItems` in der Antwort an den Admin - mit dem
// angezeigten Namen und, wo vorhanden, den waehlbaren Werten samt ihrer Zahl.
// Der Admin macht daraus eine setzbare Zeile, ohne dass hier etwas fest
// verdrahtet waere: was ein Modell meldet, kann es auch.
//
// Ein Fehlgriff kostet nichts - getItemValue wirft still und wird
// uebergangen -, ein Treffer ist eine Einstellung mehr. Deshalb ist die Liste
// lang und darf es bleiben.
export const MENU_ID_CANDIDATES = [
  // BELEGT auf einem U800F (Tizen 9, 09/2026) - diese 18 antworten mit Wert,
  // Namen und Auswahl bzw. Wertebereich:
  'auto-power-off',
  'screen-saver',
  'brightness',
  'contrast',
  'sharpness',
  'sound-mode',
  'game-mode',
  'color',
  'tint',
  'gamma',
  'shadow-detail',
  'dynamic-contrast',
  'film-mode',
  'hdmi-black-level',
  'audio-delay',
  'sound-feedback',
  'energy-saving-solution',
  'motion-lighting',
  // Die Treffer zeigen das Muster: der englische Menuetext in Kleinbuchstaben
  // mit Bindestrichen. Danach ist der Rest dieser Liste gebaut.
  //
  // Bild
  'picture-mode',
  'backlight',
  'colour',
  'colour-tone',
  'colour-temperature',
  'digital-clean-view',
  'auto-motion-plus',
  'picture-size',
  'screen-fit',
  'picture-reset',
  // Ton
  'sound-output',
  'auto-volume',
  'balance',
  'equaliser',
  'equalizer',
  'digital-output-audio-format',
  'hdmi-audio-format',
  // Timer - fuer ein Hotel die interessantesten Punkte ueberhaupt
  'sleep-timer',
  'on-timer',
  'off-timer',
  'clock',
  'clock-mode',
  'clock-set',
  'time-zone',
  'dst',
  'auto-protection-time',
  'screen-burn-protection',
  'pixel-shift',
  // Energie
  'eco-solution',
  'energy-saving',
  'eco-sensor',
  'ambient-light-detection',
  'minimum-backlight',
  'auto-power-saving',
  'standby-mode',
  'instant-on',
  'fast-start',
  'network-standby',
  'wake-on-lan',
  // Sprache und Untertitel - auf dem U800F hat davon nichts geantwortet, die
  // Zeilen bleiben trotzdem: die naechste Firmware kann es anders halten.
  'osd-language',
  'menu-language',
  'language',
  'system-manager-language',
  'audio-language',
  'first-audio-language',
  'second-audio-language',
  'subtitle',
  'subtitle-language',
  'teletext-language',
  // System / Betrieb
  'anynet',
  'anynet-hdmi-cec',
  'auto-device-detect',
  'auto-source-switch',
  'usb-access',
  'bluetooth-audio',
  'bluetooth-headphone',
  'ip-remote',
  'menu-transparency',
  'power-on-volume',
  'max-volume',
  'volume-limit',
  'power-on-source',
  'power-on-channel',
  'hotel-mode',
  'hospitality-mode',
  'menu-display',
  'home-menu-display',
  'panel-button-lock',
  'channel-edit-lock',
];

// Was die API ausser dem Wert noch ueber eine Menue-Id sagt.
//
// Der Punkt ist die Unterscheidung zwischen "diese Id gibt es nicht" und
// "diese Id gibt es, aber sie liefert keinen Wert". Auf einem U800F antwortet
// getItemValue('osd-language') nicht, obwohl Samsung genau diese Id als DEN Weg
// zur Menuesprache dokumentiert (Partner-Training S. 26-27). Kennt die
// Firmware den Namen aber und meldet den Punkt nur als unsichtbar, ist das eine
// ganz andere Aussage - dann versteckt der Hospitality-Modus den Eintrag, und
// das Setzen kann trotzdem gehen (siehe writeOnly im Katalog).
function describeMenuItem(api, id) {
  const out = {};
  try {
    if (typeof api.getItemDisplayName === 'function') {
      const name = api.getItemDisplayName(id);
      if (name) out.name = String(name);
    }
  } catch (e) {
    /* nicht jede Firmware kennt jeden Zusatz */
  }
  try {
    if (typeof api.getItemPath === 'function') {
      // getItemPath liefert eine LISTE von Menuepunkten, nicht einen Text.
      // Ein String() darueber ergab "[object Object],[object Object]" - der
      // Weg war damit gemessen und trotzdem unlesbar. Gebraucht wird die Kette
      // der Menue-Ids: mainmenu > system > osd-language.
      const path = api.getItemPath(id);
      if (path && path.length) {
        const parts = [];
        for (let i = 0; i < path.length; i += 1) {
          const step = path[i];
          if (step && typeof step === 'object') parts.push(String(step.menuid || step.id || step.name || ''));
          else parts.push(String(step));
        }
        out.path = parts.filter(Boolean).join(' > ');
      } else if (path) {
        out.path = String(path);
      }
    }
  } catch (e) {
    /* dito */
  }
  try {
    if (typeof api.isItemVisible === 'function') out.visible = !!api.isItemVisible(id);
  } catch (e) {
    /* dito */
  }
  try {
    if (typeof api.isItemEnabled === 'function') out.enabled = !!api.isItemEnabled(id);
  } catch (e) {
    /* dito */
  }
  return out;
}

/**
 * Die waehlbaren Werte eines Menuepunkts - Wert UND Name.
 *
 * Der Name ist fuer den Menschen, der Wert ist das, was setItemValue verlangt.
 * Nur die Namen zu melden hiesse, die Auswahl anzeigen zu koennen, aber nichts
 * damit setzen zu koennen - gemessen heisst "Game" beim Ton-Modus die 13,
 * nicht die 1. Bei der Menuesprache ist genau diese Liste die Antwort auf die
 * Frage, welche Sprachen das Geraet ueberhaupt anbietet.
 */
function itemOptions(api, id) {
  try {
    if (typeof api.getItemDataList !== 'function') return null;
    const list = api.getItemDataList(id);
    if (!list || !list.length) return null;
    const out = [];
    for (let i = 0; i < list.length; i += 1) {
      const o = list[i];
      if (o && typeof o === 'object') out.push({ value: o.value, name: o.name || o.osdName || String(o.value) });
      else out.push({ value: o, name: String(o) });
    }
    return out;
  } catch (e) {
    return null;
  }
}

// Ein Menuepunkt mit allem, was die API dazu hergibt. null, wenn die Firmware
// die Id gar nicht kennt.
function probeMenuItem(api, id, describeMisses) {
  let raw;
  try {
    raw = api.getItemValue(id);
  } catch (e) {
    raw = undefined;
  }
  if (raw === null || raw === undefined || raw === '') {
    // Kein Wert - aber vielleicht kennt die Firmware den Punkt trotzdem.
    //
    // NUR AUF ANFORDERUNG NACHFRAGEN. Genau hier lag der Neustart des U800F:
    // getItemDisplayName/getItemPath/isItemVisible auf einer Menue-Id, die es
    // NICHT gibt, wirft die Firmware um. Bei den 18 vorhandenen Ids laufen
    // dieselben Aufrufe unauffaellig (sie stehen im Log), bei den 69
    // unbekannten dahinter bricht der Lauf ab. Ein `describe` mit einer kurzen
    // Id-Liste erlaubt die Messung dort, wo man sie will.
    if (!describeMisses) return null;
    const known = describeMenuItem(api, id);
    // Die Auswahlliste gehoert hierher, nicht nur zu den Punkten mit Wert:
    // bei der Menuesprache ist sie das Gesuchte. Sie steht unter derselben
    // Freigabe wie die uebrigen Nachfragen.
    const options = itemOptions(api, id);
    if (options) known.options = options;
    if (
      known.name === undefined &&
      known.visible === undefined &&
      known.path === undefined &&
      !options
    )
      return null;
    known.note = 'kein Wert lesbar';
    return known;
  }

  const item = {};
  item.value = typeof raw === 'object' ? raw.value : raw;
  if (typeof raw === 'object' && raw.osdName) item.osdName = raw.osdName;

  // Der angezeigte Name und die waehlbaren Werte machen aus einer nackten 0
  // erst eine Aussage. Beides ist optional - was fehlt, fehlt.
  const known = describeMenuItem(api, id);
  if (known.name !== undefined) item.name = known.name;
  if (known.visible !== undefined) item.visible = known.visible;
  const options = itemOptions(api, id);
  if (options) item.options = options;
  try {
    if (typeof api.getSliderItemMinMaxStep === 'function') {
      const range = api.getSliderItemMinMaxStep(id);
      if (range && range.maxValue !== undefined)
        item.range = { min: range.minValue, max: range.maxValue, step: range.stepValue };
      else if (range && range.max !== undefined)
        item.range = { min: range.min, max: range.max, step: range.step };
    }
  } catch (e) {
    /* dito */
  }
  return item;
}

export default class SettingsTizen {
  static available() {
    return !!(settingsApi() || audioApi() || b2bApis().length);
  }

  /**
   * Was dieses Geraet an Einstellungs-APIs tatsaechlich mitbringt.
   *
   * Der Katalog raet Menue-Ids und Methodennamen - diese Aufzaehlung ist das
   * Gegenstueck dazu: sie sagt, was da ist. Sie geht mit in die Antwort an den
   * Admin und steht auf der Info-Seite, damit sich die Namen Modell fuer
   * Modell festhalten lassen, ohne die App anzufassen.
   */
  static apiNames() {
    const out = {};
    const audio = audioApi();
    if (audio) out['tizen.tvaudiocontrol'] = methodNames(audio);
    const settings = settingsApi();
    if (settings) out['webapis.settings'] = methodNames(settings);
    b2bApis().forEach((e) => {
      out[e.name] = methodNames(e.api);
    });
    return out;
  }

  /**
   * Die Menuepunkte, die dieses Geraet kennt - Id, Name, Wert, Auswahl.
   *
   * Synchron und damit billig; laeuft bei jeder Abfrage mit. Genau daraus wird
   * die Frage beantwortet, welche Einstellungen ein Modell ausser den bekannten
   * noch hergibt.
   */
  static extras(options) {
    const api = settingsApi();
    if (!api || !hasMethod(api, 'getItemValue')) return {};

    // `describe` sagt, bei welchen Ids ohne Wert nachgefragt werden darf:
    // true fuer alle (gefaehrlich, siehe probeMenuItem) oder eine Id-Liste.
    const describe = (options && options.describe) || false;
    const describeList = Array.isArray(describe) ? describe : null;

    const found = {};
    for (let i = 0; i < MENU_ID_CANDIDATES.length; i += 1) {
      const id = MENU_ID_CANDIDATES[i];
      const mayDescribe = describeList ? describeList.indexOf(id) !== -1 : !!describe;
      const item = probeMenuItem(api, id, mayDescribe);
      if (item) found[id] = item;
    }
    return Object.keys(found).length ? { menuItems: found } : {};
  }

  /**
   * Einen Menuepunkt setzen, den die Sonde gefunden hat (Schluessel `menu:<id>`).
   *
   * Der Katalog kennt diese Punkte nicht - sie kommen vom Geraet selbst, mit
   * Namen und waehlbaren Werten. Gesetzt wird mit genau dem Wert aus dieser
   * Liste; zurueckgelesen wird wie ueberall sonst, weil setItemValue auch dann
   * true melden kann, wenn nichts uebernommen wurde.
   */
  static writeMenuItem(id, value) {
    const api = settingsApi();
    if (!api || !hasMethod(api, 'setItemValue'))
      return Promise.resolve({ ok: false, detail: 'webapis.settings.setItemValue fehlt' });
    const before = probeMenuItem(api, id);
    if (!before) return Promise.resolve({ ok: false, detail: 'Menuepunkt "' + id + '" gibt es hier nicht' });

    const n = Number(value);
    const native = String(value).trim() !== '' && !isNaN(n) ? n : value;
    let written;
    try {
      written = api.setItemValue(id, native);
    } catch (e) {
      return Promise.resolve({
        ok: false,
        via: 'settings:' + id,
        detail: String((e && (e.message || e.name)) || e),
      });
    }
    const after = probeMenuItem(api, id);
    return Promise.resolve({
      ok: true,
      value: after ? after.value : undefined,
      via: 'settings:' + id,
      detail: 'setItemValue lieferte ' + written,
    });
  }

  // Liefert Promise<{ value, via }> oder Promise<null>.
  static read(entry) {
    const map = entry.tizen;
    if (!map) return Promise.resolve(null);

    // Ein Einzelaufruf hat keinen Ist-Wert - die Firmware gibt ihn nicht her.
    // Die Zeile steht im Admin als "nur setzbar" (siehe writeOnly).
    if (map.call) return Promise.resolve(null);

    // 1. Ton
    if (map.audio) {
      const audio = audioApi();
      if (audio) {
        try {
          if (map.audio === 'mute' && hasMethod(audio, 'isMute'))
            return Promise.resolve({ value: !!audio.isMute(), via: 'tvaudiocontrol.isMute' });
          if (map.audio === 'volume' && hasMethod(audio, 'getVolume'))
            return Promise.resolve({ value: Number(audio.getVolume()), via: 'tvaudiocontrol.getVolume' });
        } catch (e) {
          /* weiter zu den anderen Wegen */
        }
      }
    }

    // 2. B2B-Methodenpaar
    // Der B2B-Weg meldet auch, WARUM er nichts liefert. Auf einem BU800 gibt es
    // webapis.systemcontrol zwar, aber jeder Getter antwortet mit "Fehler 9" -
    // ohne diesen Grund staende im Admin nur "nicht unterstuetzt", und niemand
    // wuesste, ob die Methode fehlt oder das Modell sie verweigert.
    const pair = b2bPair(entry);
    const viaB2b = pair
      ? callGetter(pair.api, pair.getter).then((r) =>
          r.value === undefined ? { error: r.error, via: pair.via } : { value: r.value, via: pair.via }
        )
      : Promise.resolve(null);

    // 3. Menue-Ids
    return viaB2b.then((hit) => {
      if (hit && hit.value !== undefined) return hit;
      const api = settingsApi();
      // `hit` weiterreichen, nicht null: fehlt webapis.settings ganz (auf einem
      // BU800 der Fall), ging der Grund der B2B-Absage hier verloren und die
      // Zeile stand ohne Erklaerung da.
      if (!api || !hasMethod(api, 'getItemValue')) return hit || null;
      const ids = map.setting || [];
      // Warum jede Id nichts geliefert hat. Vorher fiel das unter den Tisch -
      // im Admin stand dann "nicht unterstuetzt", ohne Unterschied zwischen
      // "die Firmware kennt die Id nicht", "sie lehnt sie ab" und "sie
      // antwortet mit einem leeren Wert". Bei der Menuesprache ist genau das
      // die offene Frage.
      const misses = [];
      for (let i = 0; i < ids.length; i += 1) {
        let item;
        try {
          item = api.getItemValue(ids[i]);
        } catch (e) {
          misses.push(ids[i] + ': ' + String((e && (e.message || e.name)) || e));
          continue;
        }
        let raw = usable(item);
        // Manche Firmware liefert den Wert leer und nur den angezeigten Namen
        // ("Deutsch"). Der ist als Auskunft besser als nichts - er sagt, in
        // welcher Sprache das Geraetemenue steht.
        if (raw === undefined && item && typeof item === 'object' && item.osdName) {
          return {
            value: String(item.osdName),
            via: 'settings:' + ids[i] + ' (osdName)',
          };
        }
        if (raw === undefined) {
          misses.push(ids[i] + ': ohne Wert');
          continue;
        }
        return { value: map.locale ? codeForLocale(String(raw)) : raw, via: 'settings:' + ids[i] };
      }
      // Kein Menuepunkt - dann bleibt der Grund des B2B-Wegs stehen, falls es
      // dort einen gab, sonst der eigene.
      if (hit) return hit;
      // Menuesprache: Ersatz ueber tizen.systeminfo LOCALE (siehe
      // helpers/OsdLanguage). Die Gruende der Menue-Ids fahren mit, damit
      // sichtbar bleibt, warum der Ersatz greift.
      if (map.locale && systemLocaleValue()) {
        return {
          value: codeForLocale(systemLocaleValue()),
          via: 'systeminfo:LOCALE',
          detail: misses.length ? 'Menue-Id nicht lesbar: ' + misses.join('; ') : undefined,
        };
      }
      if (misses.length) return { via: 'settings', error: misses.join('; ') };
      return null;
    });
  }

  /**
   * Setzt einen Wert und liest ihn zurueck.
   *
   * Zurueckgelesen wird, weil manche Firmwares true melden und den Wert
   * trotzdem nicht uebernehmen - dieselbe Beobachtung, die schon bei der
   * OSD-Sprache zum Rueckvergleich gefuehrt hat. Gemeldet wird deshalb der
   * Wert, den das Geraet DANACH hat, nicht der, den wir schicken wollten.
   *
   * Geschrieben wird nur ueber den Weg, der auch gelesen hat. Ein Setter auf
   * eine Menue-Id, die das Geraet gar nicht meldet, ist ein Schuss ins Dunkle -
   * auf einem Hospitality-TV der falsche Ort dafuer.
   */
  static write(entry, value) {
    const map = entry.tizen;
    if (!map) return Promise.resolve({ ok: false, detail: 'auf Samsung nicht verfuegbar' });

    // 0. Ein einzelner Aufruf statt eines Getter/Setter-Paares.
    //
    // Es gibt Einstellungen, die die Firmware nur SETZEN laesst, weil sie kein
    // Zustand im ueblichen Sinn sind: `deleteAppPrivateInfo('ON')` merkt das
    // Loeschen der App-Daten fuer den naechsten Neustart vor. Einen Getter
    // dazu gibt es nicht - was gesetzt ist, steht deshalb hier nie als
    // Ist-Wert, und die Zeile im Admin ist "nur setzbar".
    if (map.call) {
      const hit = b2bApis().filter((e) => hasMethod(e.api, map.call.method))[0];
      if (!hit)
        return Promise.resolve({ ok: false, detail: map.call.method + ' gibt es auf diesem Modell nicht' });
      const arg = value ? map.call.on : map.call.off;
      // Laut protokollieren, BEVOR der Aufruf laeuft: dieser eine Weg kann das
      // Geraet neu starten (gemessen an einem U800F). Steht die Zeile nicht im
      // Log, ist der Aufruf selbst der letzte Schritt gewesen.
      console.log('[settings] ' + map.call.method + '("' + arg + '") - kann einen Neustart ausloesen');
      if (arg === undefined)
        return Promise.resolve({ ok: false, detail: 'fuer diesen Wert ist kein Aufruf hinterlegt' });
      try {
        const written = hit.api[map.call.method](arg);
        return Promise.resolve({
          ok: true,
          value: value,
          via: hit.name + '.' + map.call.method,
          detail: map.call.method + '("' + arg + '") lieferte ' + written,
        });
      } catch (e) {
        return Promise.resolve({
          ok: false,
          via: hit.name + '.' + map.call.method,
          detail: String((e && (e.message || e.name)) || e),
        });
      }
    }

    // 1. Ton
    if (map.audio) {
      const audio = audioApi();
      if (audio) {
        const setter = map.audio === 'mute' ? 'setMute' : 'setVolume';
        if (hasMethod(audio, setter)) {
          return callSetter(audio, setter, map.audio === 'mute' ? !!value : Number(value)).then((res) => {
            if (!res.ok) return res;
            return SettingsTizen.read(entry).then((after) => ({
              ok: true,
              value: after ? after.value : undefined,
              via: 'tvaudiocontrol.' + setter,
            }));
          });
        }
      }
    }

    return SettingsTizen.read(entry).then((current) => {
      // Ohne lesbaren Ist-Wert wird normalerweise nicht geschrieben (siehe
      // LG.js). Eine Ausnahme gibt es: `writeOnly` im Katalog.
      //
      // Sie gilt fuer die Menuesprache. Samsung dokumentiert
      // setItemValue('osd-language', 'fr_FR.utf8') ausdruecklich als DEN Weg,
      // die OSD-Sprache zu setzen (Partner-Training "TEP Web API's -
      // Settings", 06/2026) - und diese App macht das bei jedem Sprachwechsel
      // des Gastes schon so. Gemessen antwortet getItemValue derselben Menue-Id
      // auf einem U800F trotzdem nicht. Lesen und Schreiben sind hier also
      // nicht symmetrisch, und auf dem dokumentierten Schreibweg zu bestehen
      // ist richtiger, als die Sprache aus der Ferne gar nicht setzen zu
      // koennen.
      const blind = !!map.writeOnly && !!(map.setting || []).length;
      if ((!current || current.value === undefined) && !blind)
        return {
          ok: false,
          via: current && current.via,
          detail:
            'diese Firmware meldet die Einstellung nicht - nichts gesetzt' +
            (current && current.error ? ' (' + current.error + ')' : ''),
        };

      // 2. B2B-Methodenpaar, wenn der Wert von dort kam.
      const pair = b2bPair(entry);
      if (pair && current.via === pair.via) {
        if (!pair.setter)
          return { ok: false, via: current.via, detail: 'nur lesbar - kein Setter zu ' + pair.getter };
        let native = value;
        if (entry.type === SETTING_TYPE.SWITCH) native = !!value;
        else if (entry.type === SETTING_TYPE.NUMBER) native = Number(value);
        return callSetter(pair.api, pair.setter, native).then((res) => {
          if (!res.ok) return { ok: false, via: current.via, detail: res.detail };
          return SettingsTizen.read(entry).then((after) => ({
            ok: true,
            value: after ? after.value : undefined,
            via: current.via,
          }));
        });
      }

      // 3. Menue-Id
      const api = settingsApi();
      if (!api || !hasMethod(api, 'setItemValue'))
        return { ok: false, detail: 'webapis.settings.setItemValue fehlt' };
      // Ohne Lesewert (writeOnly) die erste Menue-Id des Katalogs - bei der
      // Sprache ist das die dokumentierte 'osd-language'.
      const readId =
        current && String(current.via).indexOf('settings:') === 0
          ? current.via.slice('settings:'.length)
          : '';
      const id = readId || (map.writeOnly ? (map.setting || [])[0] : '');
      if (!id) return { ok: false, via: current && current.via, detail: 'kein Schreibweg zu diesem Lesewert' };

      // Das Einstellungs-Menue rechnet durchgehend mit ZAHLEN, nicht mit
      // 'on'/'off': gemessen auf einem U800F ist screen-saver 0/1, Auto Power
      // Off 0/4/6/8 und der Ton-Modus 1/13/14/18/19/2/... Ein 'on' waere hier
      // still ins Leere gelaufen - setItemValue haette es angenommen und nichts
      // getan.
      let native = value;
      if (entry.type === SETTING_TYPE.SWITCH) native = value ? 1 : 0;
      else if (entry.type === SETTING_TYPE.NUMBER) native = Number(value);
      else if (map.locale) {
        native = localeFor(value);
        if (!native) return { ok: false, detail: 'keine Locale fuer "' + value + '" hinterlegt' };
      }

      let written;
      try {
        written = api.setItemValue(id, native);
      } catch (e) {
        return { ok: false, via: current.via, detail: String((e && (e.message || e.name)) || e) };
      }
      return SettingsTizen.read(entry).then((after) => ({
        ok: true,
        value: after ? after.value : undefined,
        via: 'settings:' + id,
        // Kommt der Rueckgabewert nur ueber die Systemsprache, zeigt er noch
        // den alten Stand: LOCALE zieht einem Setzen erst verzoegert nach.
        detail:
          'setItemValue lieferte ' +
          written +
          (after && after.via === 'systeminfo:LOCALE' ? ' - Rueckgelesen ueber Systemsprache, zieht verzoegert nach' : ''),
      }));
    });
  }
}



// WEBPACK FOOTER //
// ./src/helpers/DeviceSettings/Tizen.js